import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { AlertTriangle, LogOut, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate } from "react-router-dom";

interface AuthErrorProps {
  error: Error | null;
  onRetry?: () => void;
}

/**
 * Shown when authorization checks fail for transient reasons (network,
 * JWT refresh race, 5xx) — distinct from /unauthorized, which means the
 * server definitively said "you don't have access".
 */
export default function AuthError({ error, onRetry }: AuthErrorProps) {
  const navigate = useNavigate();

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/auth");
  };

  // Surface the underlying error so on-call can triage from a screenshot.
  const name = error?.name ?? "Error";
  const message = error?.message ?? "Unknown error";
  // Supabase RPC errors sometimes carry a code/status
  const code = (error as unknown as { code?: string; status?: number } | null)?.code;
  const status = (error as unknown as { code?: string; status?: number } | null)?.status;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-destructive/10 rounded-full p-4">
              <AlertTriangle className="h-12 w-12 text-destructive" />
            </div>
          </div>
          <CardTitle className="text-2xl">Couldn't verify your access</CardTitle>
          <CardDescription className="text-base">
            We couldn't reach the authorization service. This is usually a
            temporary network issue — not a permissions problem.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="rounded-md border bg-muted/30 p-3 text-xs font-mono break-all">
            <div><span className="text-muted-foreground">type:</span> {name}</div>
            {status !== undefined && (
              <div><span className="text-muted-foreground">status:</span> {status}</div>
            )}
            {code && (
              <div><span className="text-muted-foreground">code:</span> {code}</div>
            )}
            <div><span className="text-muted-foreground">message:</span> {message}</div>
          </div>
          <div className="flex gap-2">
            {onRetry && (
              <Button className="flex-1" onClick={onRetry}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Retry
              </Button>
            )}
            <Button variant="outline" className="flex-1" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Sign Out
            </Button>
          </div>
          <p className="text-xs text-muted-foreground text-center">
            If this keeps happening, share this screen with #atms-support.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}